/**
 * Reine Tree-Transformationen für optimistische Cache-Updates (Move/Delete). Struktur ändern,
 * dann Counts (descendantFileCount/totalSizeBytes) bottom-up neu berechnen — billiger und
 * robuster als inkrementelles Zählen entlang der Pfade. Trees sind pro Property klein.
 */
import type { PropertyFileResponse, PropertyFileTreeNode } from '@/shared/api';

/** Counts eines Node rekursiv neu berechnen (Kinder zuerst). */
export function recount(node: PropertyFileTreeNode): PropertyFileTreeNode {
  const folders = node.folders.map(recount);
  const descendantFileCount =
    node.files.length + folders.reduce((n, f) => n + f.descendantFileCount, 0);
  const totalSizeBytes =
    node.files.reduce((n, f) => n + f.sizeBytes, 0) +
    folders.reduce((n, f) => n + f.totalSizeBytes, 0);
  return { ...node, folders, descendantFileCount, totalSizeBytes };
}

/** Datei per Id aus dem Baum entfernen (Struktur), dann Counts neu berechnen. */
export function removeFileFromTree(
  root: PropertyFileTreeNode,
  fileId: string,
): PropertyFileTreeNode {
  return recount(stripFile(root, fileId));
}

function stripFile(node: PropertyFileTreeNode, fileId: string): PropertyFileTreeNode {
  return {
    ...node,
    files: node.files.filter((f) => f.id !== fileId),
    folders: node.folders.map((f) => stripFile(f, fileId)),
  };
}

/** Datei aus dem Baum ziehen (samt Objekt), ohne Counts zu berühren. */
function extractFile(
  node: PropertyFileTreeNode,
  fileId: string,
): { node: PropertyFileTreeNode; file: PropertyFileResponse | null } {
  const found = node.files.find((f) => f.id === fileId) ?? null;
  if (found) {
    return { node: { ...node, files: node.files.filter((f) => f.id !== fileId) }, file: found };
  }
  let file: PropertyFileResponse | null = null;
  const folders = node.folders.map((sub) => {
    if (file) return sub;
    const res = extractFile(sub, fileId);
    if (res.file) file = res.file;
    return res.node;
  });
  return { node: { ...node, folders }, file };
}

function insertFile(
  node: PropertyFileTreeNode,
  targetFolder: string,
  file: PropertyFileResponse,
): { node: PropertyFileTreeNode; placed: boolean } {
  if (node.path === targetFolder) {
    return { node: { ...node, files: [...node.files, file] }, placed: true };
  }
  let placed = false;
  const folders = node.folders.map((f) => {
    const res = insertFile(f, targetFolder, file);
    if (res.placed) placed = true;
    return res.node;
  });
  return { node: { ...node, folders }, placed };
}

/**
 * Datei optimistisch in einen Ziel-Ordner verschieben: aus der Quelle ziehen, `folder` setzen,
 * in den Ziel-Node einhängen, Counts neu berechnen. Existiert der Ziel-Node nicht (oder die Datei
 * nicht), bleibt der Baum unverändert bis auf das Entfernen — der Refetch korrigiert.
 */
export function moveFileInTree(
  root: PropertyFileTreeNode,
  fileId: string,
  targetFolder: string,
): PropertyFileTreeNode {
  const { node, file } = extractFile(root, fileId);
  if (!file) return root;
  const moved = { ...file, folder: targetFolder };
  const { node: inserted, placed } = insertFile(node, targetFolder, moved);
  // Ziel-Ordner nicht im Baum → Datei nicht fallenlassen; Original lassen, Refetch korrigiert.
  if (!placed) return root;
  return recount(inserted);
}
